import {
  Component, ElementRef, Input, ViewChild, AfterViewInit, Output, EventEmitter,
  ErrorHandler, OnChanges, SimpleChanges, ChangeDetectorRef
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatAutocompleteTrigger } from '@angular/material';
import * as isString from 'lodash/isString';

import { SearchFormType } from '../models/search-form-type.model';
import {
  AutocompleteOption, AutocompleteOptionType,
  AutocompleteTriggerComponent,
  AutocompleteType
} from '../../shared/autocomplete-trigger/autocomplete-trigger.component';
import { SearchQuery } from '../models/search-query.model';
import { GoogleService } from '../../google/google.service';
import { mergeStates, State } from '../../shared/state-view/state.model';
import { AirportService } from '../../airport/airport.service';
import { AutocompleteOverlayComponent } from '../../shared/autocomplete-overlay/autocomplete-overlay.component';
import { browser } from '../../../browser';
import { Source } from '../../shared/models/source.model';
import { LatLon } from '../../util/geodesy/latlon';


@Component({
  selector: 'app-search-form',
  templateUrl: './search-form.component.html',
  styleUrls: ['./search-form.component.scss']
})
export class SearchFormComponent implements AfterViewInit, OnChanges {
  @Input() query: SearchQuery;
  @Input() activityState: State;
  @Output() queryChanged = new EventEmitter<SearchQuery>();
  @Output() locationStateChanged = new EventEmitter<State>();
  @Output() error = new EventEmitter<string>();

  State = State;
  viewReady = false;
  autocompleteState: State = State.Empty;
  AutocompleteType = AutocompleteType;
  locationState: State = State.Empty;

  @ViewChild('wrapper') wrapper: ElementRef;
  @ViewChild('queryField', { read: ElementRef }) queryFieldEl: ElementRef;
  @ViewChild('queryField', { read: MatAutocompleteTrigger }) acInputTrigger: MatAutocompleteTrigger;
  @ViewChild('acTrigger', { read: AutocompleteTriggerComponent }) acTrigger: AutocompleteTriggerComponent;
  @ViewChild('acOverlay') acOverlay: AutocompleteOverlayComponent;

  form = new FormGroup ({
    wrapper: new FormGroup({
      query: new FormControl('', [
        Validators.minLength(3)
      ])
    })
  });

  get queryCtrl(): AbstractControl {
    return this.form.get('wrapper').get('query');
  }

  get searchState(): State {
    return mergeStates(this.autocompleteState, this.locationState);
  }

  get searchErrorMsg(): string {
    if (this.autocompleteState === State.Error) {
      return 'An error occurred searching for airports or locations. Please try again later.';
    }
    if (this.locationState === State.Error) {
      return 'An error occurred resolving the location latitude and longitude. Please try again later.';
    }
    return null;
  }

  constructor(
    private _airportService: AirportService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _google: GoogleService,
    private _cdref: ChangeDetectorRef,
    private _errorHandler: ErrorHandler
  ) {}

  ngAfterViewInit() {
    setTimeout(() => {
      // Workaround to prevent ExpressionChangedAfterItHasBeenCheckedError when accessing the number of selected filters
      // in the <app-airport-filter-list> and <app-activity-filter-list> components. The viewReady flag prevents
      // rendering the filter bar until after the view has been fully initialized.
      this.viewReady = true;

      // Set initial state of the query field
      this.queryFieldEl.nativeElement.value = this.query.sanitizedTerm;

      // Workaround to prevent the invisible autocomplete menu overlay element from interfering with pointer
      // interactions on initial load.
      this.acInputTrigger.closePanel();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.query && this.query.term) {
      this.queryFieldEl.nativeElement.value = this.query.term;
    }
  }

  enableForm() {
    this.setEnabledState(true);
  }

  disableForm() {
    this.setEnabledState(false);
  }

  setEnabledState(enabled: boolean) {
    this.wrapper.nativeElement.disabled = !enabled;
  }

  setQueryFieldValue(value: string) {
    this.queryFieldEl.nativeElement.value = value;
  }

  onQueryFocus(e: Event) {
    if (browser && (browser.os === 'iOS' || browser.os === 'Android OS')) {
      // Angular Material's autocomplete component is not really usable on mobile:
      // https://github.com/angular/material2/issues/6341
      // Instead, pop up a full-screen overlay with a search box and a filtered list of suggestions
      e.preventDefault();
      this.acOverlay.show();
      this._cdref.detectChanges();
      this.acOverlay.focus();
    }
  }

  onQueryBlur() {
    // Workaround to prevent the invisible autocomplete menu overlay element from interfering with pointer interactions.
    setTimeout(() => this.acInputTrigger.closePanel(), 300);
  }

  onQueryKeyPress() {
    setTimeout(() => {
      if (isString(this.queryCtrl.value)) {
        this.query.term = this.queryCtrl.value;
      }
    }, 0);
  }

  onSubmit() {
    // Wrap in setTimeout because updating the query term is also asynchronous (see onQueryKeyPress).
    // If we were to execute the contents of _onSubmit right away, we would be using an outdated search term.
    setTimeout(() => this._onSubmit(), 30);
  }

  private async _onSubmit() {
    // This handler only fires when hitting Enter without making a selection in the autocomplete dropdown. The
    // behavior varies based on search mode:
    //   - Airport search: If the term is an exact match on airport identifier, it should take the user to the
    //     airport detail page of the corresponding airport. Otherwise, it should do a regular text search (which
    //     will search airport identifier, name, or city).
    //   - Activity search: Hitting enter should not do anything in this mode.
    if (this.query.type === SearchFormType.Activities || this.query.type === SearchFormType.AviationEvents) {
      return;
    }

    // First clear any previously selected location or bounds.
    this.query.location = null;
    this.query.bounds = null;

    // Serialize the search query
    this.queryChanged.emit(this.query);

    // Check if the search term is an exact match on an airport identifier. Only ICAO/FAA identifiers count —
    // never our internal numeric airport id (see AirportService.isAirportIdentifier); a numeric term falls
    // through to a text search.
    const term = this.query.sanitizedTerm.toUpperCase();
    if (term && AirportService.isAirportIdentifier(term)) {
      const result = await this._airportService.getAirport(term);
      if (result) {
        // Found a matching airport - take the user to the detail page
        if (result.sourceId === Source.CaribbeanGuide || result.sourceId === Source.BahamasGuide) {
          this._router.navigate(['/airports',  term, 'island']);
        } else {
          this._router.navigate(['/airports', term]);
        }
        return;
      }
    }

    // Did not find a matching airport. Close autocomplete dropdown and perform a text search.
    this.acInputTrigger.closePanel();
  }

  onItemSelected (e: AutocompleteOption) {
    switch (e.type) {
      case AutocompleteOptionType.Airport:
        if (this.query.type === SearchFormType.AviationEvents) {
          this.query.term = e.airport.identifier;
          this.query.location = new LatLon(e.airport.location.lat, e.airport.location.lon);
          this.query.radius = 50;
          const params = this.query.encode();
          this._router.routeReuseStrategy.shouldReuseRoute = function () {
            return false;
          };
          // this._router.onSameUrlNavigation = 'reload';
          this._router.navigate(['/search'], {queryParams: params});
        } else {
          this._router.navigate(['/airports', e.airport.identifier ]);
        }
        break;
      case AutocompleteOptionType.Location:
        this._resolveLocation(e.location.place_id, e.location.description);
        break;
      case AutocompleteOptionType.State:
        this.queryFieldEl.nativeElement.value = e.state.name;
        this.query.term = '';
        this.query.bounds = null;
        this.query.location = null;
        this.query.radius = null;
        this.query.locationDescription = e.state.name;
        this.query.state = e.state.stateCode;
        this._updateLocationState(State.Content);
        this.queryChanged.emit(this.query);
        break;
      default:
        // Perform a text search
        this.query.term = e.query;
        this.query.location = null;
        this.query.bounds = null;
        this.query.state = null;
        this.queryChanged.emit(this.query);
        this.acInputTrigger.closePanel();
        this.setQueryFieldValue(e.query);
    }
  }

  private async _resolveLocation(placeId: string, description: string) {
    this._updateLocationState(State.Loading);
    this.disableForm();
    try {
      const location = await this._google.geocodePlace(placeId);
      this.query.term = '';
      this.query.bounds = null;
      this.query.state = null;
      this.query.location = location;
      this.query.locationDescription = description || '';
      this.query.radius = this.query.radius || 50;
      this._updateLocationState(State.Content);
      this.queryChanged.emit(this.query);
    } catch (e) {
      this._errorHandler.handleError(e);
      this._updateLocationState(State.Error);
      this.error.emit('An error occurred resolving the location latitude and longitude. Please try again later.');
    } finally {
      this.enableForm();
    }
  }

  private _updateLocationState(state: State) {
    this.locationState = state;
    this.locationStateChanged.emit(state);
  }
}
